import React, { useId, useState } from 'react';
import Button from './ui/Button';
import FieldSelect from './ui/FieldSelect';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';

/**
 * FS-504: the ONE identity + pressure-curve field block, shared by the
 * proposal review form, the manual-entry form (both in LabelProposalsPanel)
 * and the catalog row editor (FilterRowEditor). The Model name field lives
 * here and nowhere else — it is the human display name that replaced the
 * generated FILTER_LABEL:* keys.
 *
 * All values are string drafts while typing; callers convert on submit.
 * Depth is a select of the matcher buckets {1,2,3,4,5,6}: any other value
 * publishes a row no device can ever match. A non-bucket incoming value
 * (app-submitted photo depths) is rendered as an extra flagged option so
 * the reviewer sees what the label said and must actively choose a bucket.
 */

export interface IdentityCurveDraft {
  brand: string;
  manufacturer: string;
  model: string;
  nominal_size: string;
  depth_inches: string;
  merv: string;
  maedb_model_number: string;
  curve: { resistance_iwc: string; cfm: string }[];
}

export const MATCHABLE_DEPTHS = ['1', '2', '3', '4', '5', '6']; // FS-534: 3" added; mirrors the matcher's buckets

/** The six fields that make a publication identity — what a server-side
 * `identity_errors` map can name. Manufacturer and the curve are not
 * identity. */
export type IdentityFieldName = Exclude<keyof IdentityCurveDraft, 'manufacturer' | 'curve'>;

/** FS-674: which cells of one curve row the label reader flagged. Painted
 * as a red outline until the cell is edited; a separate per-row state from
 * the identity-only `fieldErrors`. */
export interface CurveCellFlags {
  resistance_iwc: boolean;
  cfm: boolean;
}

/** Default human model name, mirroring the server's humanLabelModel():
 * the MAEDbS number verbatim when present, else "MERV {n}". */
export const defaultModelName = (draft: Pick<IdentityCurveDraft, 'maedb_model_number' | 'merv'>): string => {
  const maedb = draft.maedb_model_number.trim();
  if (maedb) return maedb;
  const merv = draft.merv.trim();
  return merv ? `MERV ${merv}` : '';
};

const FilterIdentityCurveFields: React.FC<{
  value: IdentityCurveDraft;
  onChange: (next: IdentityCurveDraft) => void;
  showManufacturer?: boolean;
  /** Distinguishes co-rendered instances' aria labels ('' | 'Reviewed ' | 'Edit '). */
  ariaPrefix?: string;
  /** FS-663: per-field errors (the review panel's `identity_errors`),
   * painted red under the field they name. */
  fieldErrors?: Partial<Record<IdentityFieldName, string>>;
  /** FS-663: rendered under the Brand field (the unknown-brand line, its
   * suggestions and the new-brand acknowledgment). */
  brandSlot?: React.ReactNode;
  /** FS-663 (F5): freeze every field — the review panel sets it while an
   * accept is in flight, so the identity the server is deciding on cannot
   * change under it. */
  disabled?: boolean;
  /** FS-674: per-row cell flags from a failed read (Correct by hand). The
   * caller keeps them in step with the rows; a flagged cell is outlined
   * red and marked `data-flagged`. */
  curveFlags?: CurveCellFlags[];
}> = ({
  value, onChange, showManufacturer = false, ariaPrefix = '',
  fieldErrors = {}, brandSlot = null, disabled = false, curveFlags = [],
}) => {
  const set = (field: keyof IdentityCurveDraft, v: string) =>
    onChange({ ...value, [field]: v });

  const setCurvePoint = (idx: number, field: 'resistance_iwc' | 'cfm', v: string) =>
    onChange({
      ...value,
      curve: value.curve.map((pt, i) => (i === idx ? { ...pt, [field]: v } : pt)),
    });

  const addCurvePoint = () =>
    onChange({ ...value, curve: [...value.curve, { resistance_iwc: '', cfm: '' }] });

  const removeCurvePoint = (idx: number) =>
    onChange({ ...value, curve: value.curve.filter((_, i) => i !== idx) });

  const depthIsBucket = MATCHABLE_DEPTHS.includes(value.depth_inches);
  const modelPlaceholder = defaultModelName(value);
  const fieldId = useId();
  // Base UI needs the old option through selection commit. Tie that option to
  // the draft's other fields and refresh it before every menu opening.
  const draftKey = JSON.stringify([
    value.brand, value.manufacturer, value.model, value.nominal_size,
    value.merv, value.maedb_model_number, value.curve,
  ]);
  const [depthAtOpen, setDepthAtOpen] = useState({ draftKey, depth: '' });
  const retainedDepth = depthAtOpen.draftKey === draftKey ? depthAtOpen.depth : '';
  const unmatchedDepth = !depthIsBucket ? value.depth_inches : retainedDepth;
  const onDepthOpenChange = (open: boolean) => {
    if (open) setDepthAtOpen({ draftKey, depth: depthIsBucket ? '' : value.depth_inches });
  };
  const FLAGGED = 'outline-solid outline-2 -outline-offset-1 outline-destructive';
  const flagClass = (i: number, field: keyof CurveCellFlags) =>
    (curveFlags[i]?.[field] ? ` ${FLAGGED}` : '');

  const inputField = (
    field: Exclude<keyof IdentityCurveDraft, 'curve' | 'depth_inches'>,
    label: string,
    help: string,
    type: 'text' | 'number' = 'text',
    placeholder?: string,
  ) => {
    const id = `${fieldId}-${field}`;
    const error = field !== 'manufacturer' ? fieldErrors[field] : undefined;
    return (
      <div>
        <Label htmlFor={id} className="block text-xs mb-0.5">{label}</Label>
        <Input id={id} type={type} value={value[field]} disabled={disabled}
          placeholder={placeholder} aria-label={`${ariaPrefix}${label.replace(/ \*$/, '').replace(/ \(optional\)$/, '')}`}
          aria-invalid={!!error} aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
          onChange={e => set(field, e.target.value)} />
        <span id={`${id}-help`} className="block text-xs text-muted-foreground">{help}</span>
        {error && <span id={`${id}-error`} className="block text-xs text-destructive" role="alert">{error}</span>}
        {field === 'brand' && brandSlot}
      </div>
    );
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-3">
        {inputField('brand', 'Brand *', 'Brand shown in the catalog and used to match filters.')}
        {showManufacturer && inputField('manufacturer', 'Manufacturer', 'Company that made this filter.')}
        {inputField('model', 'Model name', `Shown in the catalog. Blank uses ${modelPlaceholder ? `"${modelPlaceholder}"` : 'the MAEDbS number or MERV'}.`, 'text', modelPlaceholder || 'e.g. 210')}
        {inputField('nominal_size', 'Size (LxWxD) *', 'Nominal length, width and depth used for matching.', 'text', 'e.g. 16x25x1')}
        <FieldSelect id={`${fieldId}-depth_inches`} label="Depth (in) *"
          ariaLabel={`${ariaPrefix}Depth (in)`}
          help="Choose a matchable depth. A submitted depth outside these buckets must be corrected."
          value={value.depth_inches} disabled={disabled}
          error={fieldErrors.depth_inches}
          onOpenChange={onDepthOpenChange}
          onChange={next => set('depth_inches', next)}
          options={[
            { value: '', label: '— pick a depth —' },
            ...(unmatchedDepth ? [{ value: unmatchedDepth, label: `${unmatchedDepth}" (as submitted — not matchable)` }] : []),
            ...MATCHABLE_DEPTHS.map(d => ({ value: d, label: `${d}"` })),
          ]} />
        {inputField('merv', 'MERV *', 'Minimum efficiency reporting value used for matching.', 'number')}
        {inputField('maedb_model_number', 'MAEDbS model (optional)', 'Official MAEDbS model number when available.')}
      </div>

      <h5 className="text-sm font-bold mb-1">Pressure curve * (min 2 points)</h5>
      <p id={`${fieldId}-curve-help`} className="text-xs text-muted-foreground mb-1">
        Each point pairs filter resistance in inches of water column with airflow in cubic feet per minute.
      </p>
      <table className="table-auto text-xs border-collapse border border-border mb-2">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-2 py-1 border">Resistance (IWC)</th>
            <th className="px-2 py-1 border">CFM</th>
            <th scope="col" className="px-2 py-1 border">Actions</th>
          </tr>
        </thead>
        <tbody>
          {value.curve.map((pt, i) => (
            <tr key={i}>
              <td className="px-1 py-0.5 border">
                <Label htmlFor={`${fieldId}-resistance-${i}`} className="sr-only">{`${ariaPrefix}Resistance (IWC) point ${i + 1}`}</Label>
                <Input id={`${fieldId}-resistance-${i}`} type="number" step="0.01" value={pt.resistance_iwc}
                  className={`w-24 text-xs${flagClass(i, 'resistance_iwc')}`} disabled={disabled}
                  data-flagged={curveFlags[i]?.resistance_iwc ? 'true' : 'false'}
                  aria-invalid={Boolean(curveFlags[i]?.resistance_iwc)}
                  aria-label={`${ariaPrefix}Resistance (IWC) point ${i + 1}`}
                  aria-describedby={`${fieldId}-curve-help`}
                  title="Resistance in inches of water column for this curve point"
                  onChange={e => setCurvePoint(i, 'resistance_iwc', e.target.value)} />
              </td>
              <td className="px-1 py-0.5 border">
                <Label htmlFor={`${fieldId}-cfm-${i}`} className="sr-only">{`${ariaPrefix}CFM point ${i + 1}`}</Label>
                <Input id={`${fieldId}-cfm-${i}`} type="number" value={pt.cfm}
                  className={`w-24 text-xs${flagClass(i, 'cfm')}`} disabled={disabled}
                  data-flagged={curveFlags[i]?.cfm ? 'true' : 'false'}
                  aria-invalid={Boolean(curveFlags[i]?.cfm)}
                  aria-label={`${ariaPrefix}CFM point ${i + 1}`}
                  aria-describedby={`${fieldId}-curve-help`}
                  title="Airflow in cubic feet per minute for this curve point"
                  onChange={e => setCurvePoint(i, 'cfm', e.target.value)} />
              </td>
              <td className="px-1 py-0.5 border text-center">
                <Button variant="icon" size="sm" onClick={() => removeCurvePoint(i)} disabled={disabled}
                  aria-label={`${ariaPrefix}Remove point ${i + 1}`}
                  title="Take this point off the curve. The change is kept only when you save.">✕</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="link" size="sm" onClick={addCurvePoint} disabled={disabled}
        title="Add an empty row to the resistance curve for another resistance and CFM pair."
        className="mb-3">+ add point</Button>

    </>
  );
};

export default FilterIdentityCurveFields;
